import { useCallback, useState } from "react";

import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { EarningsDetailPanel } from "./-components/earnings-detail-panel";
import { EarningsMainPanel } from "./-components/earnings-main-panel";
import { EarningsSidebar } from "./-components/earnings-sidebar";

import {
  fetchEarningsSummary,
  fetchWorkerPayments,
  fetchSpendingsSummary,
  fetchBusinessPayments,
} from "@/queries/api/earningsApi";
import { useAuth } from "@/context/auth";
import type { Role } from "@/utils/hooks/use-role";
import { QueryKeys } from "@/queries/queryKeys";
import { getBusinessProfile } from "@/queries/api/businessApi";
import { useGetBusinessProfileQuery } from "@/queries/hooks/businessHooks";
import { startOfWeek, endOfWeek } from "date-fns";

import { useIsMobile } from "@/utils/hooks/use-is-mobile";

function getWeekRange(date = new Date()) {
  // Get Monday of current week
  const d = new Date(date);
  const monday = startOfWeek(d, { weekStartsOn: 1 });
  const sunday = endOfWeek(d, { weekStartsOn: 1 });
  return {
    start: monday.toISOString(),
    end: sunday.toISOString(),
  };
}

function getPaymentsSummary(role: Role) {
  return {
    queryKey: [QueryKeys.PAYMENTS, "summary", role],
    queryFn: async () => {
      const fn =
        role === "worker" ? fetchEarningsSummary : fetchSpendingsSummary;
      const response = await fn();
      if (!response.success) {
        throw new Error("Invalid summary response");
      }
      return response;
    },
  };
}

function getPayments(role: Role, userId: string, start: string, end: string) {
  return {
    queryKey: [QueryKeys.PAYMENTS, "chart", role, start, end],
    queryFn: async () => {
      const fn =
        role === "worker"
          ? () => fetchWorkerPayments({ workerId: userId, start, end })
          : () => fetchBusinessPayments({ businessId: userId, start, end });
      const response = await fn();
      return {
        payments: response.payments,
        assignments: response.assignments || [],
      };
    },
  };
}

export const Route = createFileRoute("/app/$role/_app/payments/")({
  loader: async ({ context }) => {
    const user = context.auth?.user;
    if (!user) {
      return { summary: null, payments: null };
    }
    const { start, end } = getWeekRange();
    context.queryClient.ensureQueryData(getPaymentsSummary(user.role));
    if (user.role === "employer") {
      await context.queryClient.ensureQueryData({
        queryKey: [QueryKeys.BUSINESS_PROFILE],
        queryFn: getBusinessProfile,
      });
      const businessData = context.queryClient.getQueryData([
        QueryKeys.BUSINESS_PROFILE,
      ]);
      const businessId = (businessData as any).business.id.toString();
      context.queryClient.ensureQueryData(
        getPayments(user.role, businessId, start, end),
      );
    } else {
      context.queryClient.ensureQueryData(
        getPayments(user.role, user.userId, start, end),
      );
    }
  },
  component: EarningsPage,
});

export default function EarningsPage() {
  const { t } = useTranslation("payments");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const auth = useAuth();
  const role = auth.user!.role;
  const isMobile = useIsMobile();

  const { data: businessProfile } = useGetBusinessProfileQuery({
    enabled: role === "employer",
  });
  const { start, end } = useCallback(() => getWeekRange(), [])();

  const { data: summary } = useSuspenseQuery(getPaymentsSummary(role));
  const {
    data: { payments, assignments },
  } = useSuspenseQuery(
    getPayments(
      role,
      role === "worker"
        ? auth.user!.userId
        : businessProfile?.business.id?.toString() || "",
      start,
      end,
    ),
  );

  // Find selected item from summary
  const selectedItem = selectedId
    ? summary.payments.find((p) => String(p.payment_id) === selectedId)
    : null;

  // Determine terminology
  const mode = auth.user!.role === "worker" ? "worker" : "employer";
  const label = mode === "worker" ? t("earnings") : t("spendings");

  if (isMobile) {
    // Mobile stacked view: show either sidebar or detail panel
    if (selectedId == null) {
      return (
        <div className="h-full w-full flex flex-col px-4 pt-4">
          <div className="text-lg font-bold text-garnet mb-4">{label}</div>
          <EarningsSidebar
            selectedId={selectedId}
            onSelect={setSelectedId}
            summary={summary.payments}
            mode={mode}
          />
          <section className="flex-1 min-h-[300px] mt-4">
            <EarningsMainPanel payments={payments} summary={summary} assignments={assignments} />
          </section>
        </div>
      );
    }
    // Show detail panel stacked above, with small back button
    return (
      <div className="h-full w-full flex flex-col px-4 pt-4 bg-white">
        <div className="flex items-center mb-4">
          <button
            type="button"
            aria-label={t("navigation.back")}
            onClick={() => setSelectedId(null)}
            className="mr-2 rounded-full p-2 bg-gray-100 hover:bg-gray-200"
            style={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#545A63"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="font-bold text-garnet text-lg">{label}</div>
        </div>
        <EarningsDetailPanel
          item={selectedItem}
          onBack={() => setSelectedId(null)}
          mode={mode}
          label={label}
        />
      </div>
    );
  }

  // Desktop: keep two-column layout
  return (
    <div className="flex h-full w-full px-8 pt-8 gap-8">
      {selectedId == null ? (
        <EarningsSidebar
          selectedId={selectedId}
          onSelect={setSelectedId}
          summary={summary.payments}
          mode={mode}
        />
      ) : (
        <EarningsDetailPanel
          item={selectedItem}
          onBack={() => setSelectedId(null)}
          mode={mode}
          label={label}
        />
      )}
      <section className="flex-1 min-h-[600px]">
        <EarningsMainPanel payments={payments} summary={summary} assignments={assignments} />
      </section>
    </div>
  );
}
